import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input from "src/components/inputs/Input";
import TimePicker from "src/components/time/TimePicker";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  GENDERS,
  LEAD_STATUS_OPTIONS,
  LEAD_TYPE,
  QUERY_SOURCES,
} from "src/utils/constants";
import { number, object, string } from "yup";
import {
  useCreateEnquiry,
  useGetEnquiryData,
  useUpdateEnquiry,
} from "../api-clients";

const FORM_FIELDS = {
  NAME: "name",
  CONTACT: "phone",
  EMAIL: "email",
  GENDER: "gender",
  ADDRESS: "address",
  QUERY: "query",
  NEXT_FOLLOW_UP_DATE: "followUpDate",
  NEXT_FOLLOW_UP_TIME: "followUpTime",
  QUERY_SOURCE: "source",
  LEAD_TYPE: "leadType",
  LEAD_STATUS: "leadStatus",
};

const CreateEnquiry = ({
  isInViewMode = false,
}: {
  isInViewMode?: boolean;
}) => {
  useSetActiveTab(TAB_NAMES.ENQUIRY_MANAGEMENT);
  const navigate = useNavigate();
  const { enquiryId: id } = useParams();

  const enquiryId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!enquiryId, [enquiryId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetCourseLoading, data } = useGetEnquiryData(enquiryId);

  const enquiryData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateCourse,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateEnquiry(enquiryId);

  const { isLoading, isSuccess, execute, error } = useCreateEnquiry();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: enquiryData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.CONTACT]: enquiryData?.[FORM_FIELDS.CONTACT] ?? null,
      [FORM_FIELDS.EMAIL]: enquiryData?.[FORM_FIELDS.EMAIL] ?? null,
      [FORM_FIELDS.GENDER]: enquiryData?.[FORM_FIELDS.GENDER] ?? null,
      [FORM_FIELDS.ADDRESS]: enquiryData?.[FORM_FIELDS.ADDRESS] ?? null,
      [FORM_FIELDS.QUERY]: enquiryData?.[FORM_FIELDS.QUERY] ?? null,
      [FORM_FIELDS.NEXT_FOLLOW_UP_DATE]:
        enquiryData?.[FORM_FIELDS.NEXT_FOLLOW_UP_DATE] ?? null,
      [FORM_FIELDS.NEXT_FOLLOW_UP_TIME]:
        enquiryData?.[FORM_FIELDS.NEXT_FOLLOW_UP_TIME] ?? null,
      [FORM_FIELDS.QUERY_SOURCE]:
        enquiryData?.[FORM_FIELDS.QUERY_SOURCE] ?? null,
      [FORM_FIELDS.LEAD_TYPE]: enquiryData?.[FORM_FIELDS.LEAD_TYPE] ?? null,
      [FORM_FIELDS.LEAD_STATUS]: enquiryData?.[FORM_FIELDS.LEAD_STATUS] ?? null,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.CONTACT]: number()
        .nullable()
        .required("This is a Required field."),
      [FORM_FIELDS.EMAIL]: string()
        .email("Invalid email")
        .required("This is a Required field."),
      [FORM_FIELDS.QUERY]: string().required("This is a Required field."),
      [FORM_FIELDS.NEXT_FOLLOW_UP_TIME]: string().required(
        "This is a Required field."
      ),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateCourse(data);
        return;
      }
      execute(data);
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched } = formik;

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Enquiry Updated" : "Enquiry Created."
      );
      navigate("/store/enquiries");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetCourseLoading || isUpdateLoading,
    [isGetCourseLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Enquiry" showBreadcrumb />

      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Name"
            placeholder="Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isFieldDisabled}
          />

          <Input
            type="number"
            label="Contact Number"
            placeholder="Contact Number"
            name={FORM_FIELDS.CONTACT}
            value={values[FORM_FIELDS.CONTACT] as string}
            error={getFieldError(FORM_FIELDS.CONTACT)}
            onChange={(e) => setFieldValue(FORM_FIELDS.CONTACT, e.target.value)}
            disabled={isFieldDisabled}
            required
          />

          <Input
            type="email"
            label="Email"
            placeholder="Email"
            name={FORM_FIELDS.EMAIL}
            value={values[FORM_FIELDS.EMAIL] as string}
            error={getFieldError(FORM_FIELDS.EMAIL)}
            onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
            disabled={isFieldDisabled}
            required
          />

          <Dropdown
            label="Gender"
            placeholder="Select Gender"
            className={"w-full"}
            options={GENDERS}
            value={values[FORM_FIELDS.GENDER] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.GENDER, val)}
            disabled={isFieldDisabled}
          />

          <Input
            label="Address"
            placeholder="Address"
            name={FORM_FIELDS.ADDRESS}
            value={values[FORM_FIELDS.ADDRESS] as string}
            error={getFieldError(FORM_FIELDS.ADDRESS)}
            onChange={(e) => setFieldValue(FORM_FIELDS.ADDRESS, e.target.value)}
            disabled={isFieldDisabled}
          />

          <Input
            label="Query"
            placeholder="Query"
            name={FORM_FIELDS.QUERY}
            value={values[FORM_FIELDS.QUERY] as string}
            error={getFieldError(FORM_FIELDS.QUERY)}
            onChange={(e) => setFieldValue(FORM_FIELDS.QUERY, e.target.value)}
            disabled={isFieldDisabled}
            required
          />

          <DatePicker
            label="Next Follow-up date"
            value={values[FORM_FIELDS.NEXT_FOLLOW_UP_DATE]}
            onChange={(value) =>
              setFieldValue(FORM_FIELDS.NEXT_FOLLOW_UP_DATE, value)
            }
            disabled={isFieldDisabled}
          />

          <TimePicker
            label="Next Follow-up Time"
            value={values[FORM_FIELDS.NEXT_FOLLOW_UP_TIME]}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.NEXT_FOLLOW_UP_TIME, e.target.value)
            }
            error={getFieldError(FORM_FIELDS.NEXT_FOLLOW_UP_TIME)}
            disabled={isFieldDisabled}
            required
          />

          <Dropdown
            label="Source of Query"
            placeholder="Select Source"
            className={"w-full"}
            options={QUERY_SOURCES}
            value={values[FORM_FIELDS.QUERY_SOURCE] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.QUERY_SOURCE, val)}
            disabled={isFieldDisabled}
          />

          <Dropdown
            label="Lead Type"
            placeholder="Select Type"
            className={"w-full"}
            options={LEAD_TYPE}
            value={values[FORM_FIELDS.LEAD_TYPE] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.LEAD_TYPE, val)}
            disabled={isFieldDisabled}
          />

          <Dropdown
            label="Lead Status"
            placeholder="Select Status"
            className={"w-full"}
            options={LEAD_STATUS_OPTIONS}
            value={values[FORM_FIELDS.LEAD_STATUS] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.LEAD_STATUS, val)}
            disabled={isFieldDisabled}
          />
        </div>

        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateEnquiry;
